import { ApolloError, useApolloClient } from '@apollo/client';
import debounce from 'awesome-debounce-promise';
import type { PropsWithChildren } from 'react';
import React, { useCallback, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { v4 as uuid } from 'uuid';

import type {
  AuthenticationInfoQuery,
  AuthenticationInfoQueryVariables,
  BeaconUserQuery,
  BeaconUserQueryVariables,
} from 'src/shared/graphql/generated';
import {
  AuthenticationInfoDocument as authenticationInfoQuery,
  BeaconUserDocument as beaconUserQuery,
} from 'src/shared/graphql/generated';

import type { AuthenticationMethodContext } from '../../hooks/useAuthenticationMethod';
import { Provider as AuthenticationMethodProvider } from '../../hooks/useAuthenticationMethod';

import { useAuthenticationMethodState } from './state';

const BEACON_LOGIN_URI = 'https://beaconproplus.com/hover_explicit_login';
const QUERY_DEBOUNCE_WAIT_MILLISECONDS = 250;

type ProviderProps = PropsWithChildren<
  Pick<AuthenticationMethodContext, 'activeMethod' | 'redirectPath'>
>;
interface AuthenticationMethodOptions {
  beacon: boolean;
  singleSignOn: boolean;
}

const generateBeaconLoginUrl = (email: string) =>
  `${BEACON_LOGIN_URI}?ua=desktop&email=${encodeURIComponent(
    email,
  )}&uuid=${encodeURIComponent(uuid())}`;

const Provider = ({ children, activeMethod, redirectPath }: ProviderProps) => {
  const client = useApolloClient();
  const { context, fail, reset, start, update } =
    useAuthenticationMethodState();

  const checkEmail = debounce(
    async (
      email: string,
      { beacon, singleSignOn }: AuthenticationMethodOptions,
    ) => {
      try {
        start(email);

        const singleSignOnRequest = singleSignOn
          ? client.query<
              AuthenticationInfoQuery,
              AuthenticationInfoQueryVariables
            >({
              query: authenticationInfoQuery,
              variables: { email, redirectPath },
            })
          : Promise.resolve(null);

        const beaconRequest = beacon
          ? client.query<BeaconUserQuery, BeaconUserQueryVariables>({
              query: beaconUserQuery,
              variables: { email },
            })
          : Promise.resolve(null);

        const [singleSignOnResult, beaconResult] = await Promise.all([
          singleSignOnRequest,
          beaconRequest,
        ]);

        const {
          ssoEnabled: isSingleSignOnEnabled = false,
          ssoSpSigninUrl: singleSignOnUrl = null,
        } = singleSignOnResult?.data.authenticationInfo ?? {};
        const isBeaconEnabled =
          beaconResult?.data.beaconUser.beacon_user ?? false;

        // flushSync is needed here to allow the login form to update the onSubmit handler with the updated values prior
        // to the onSubmit handler being called. This became an issue with React 18 due to how state batching was
        // changed.
        flushSync(() =>
          update({
            isSingleSignOnEnabled,
            singleSignOnUrl,
            isBeaconEnabled,
            beaconLoginUrl: isBeaconEnabled
              ? generateBeaconLoginUrl(email)
              : null,
          }),
        );

        return isSingleSignOnEnabled || isBeaconEnabled;
      } catch (thrown) {
        fail(
          thrown instanceof ApolloError
            ? thrown
            : new Error(
                'An unknown error occurred attempting to detect authentication method',
              ),
        );

        return false;
      }
    },
    QUERY_DEBOUNCE_WAIT_MILLISECONDS,
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkEmailMemoized = useCallback(checkEmail, [client]);

  // This prevents a stale error from hanging around when the
  // user switches between SSO, Beacon, and password-based login
  useEffect(() => reset(), [activeMethod, reset]);

  const contextValue = {
    ...context,
    activeMethod,
    checkEmail: checkEmailMemoized,
  };

  return (
    <AuthenticationMethodProvider value={contextValue}>
      {children}
    </AuthenticationMethodProvider>
  );
};

export { Provider };
